/**
 * 选择→修改（边界/平滑/扩展/收缩/羽化）对话框（DESIGN.md §6.2 补充）。
 *
 * 五种操作共享同一个「输入像素数」外形，只有标题和默认值不同——
 * 不为每种操作各开一个组件。
 */
import type { JSX } from 'solid-js';
import { Show, createEffect, createSignal } from 'solid-js';
import { useFocusTrap } from '../a11y/focus-trap';
import type { SelectionModifyKind } from '../../app/actions/selection-modify-actions';
import styles from './SelectionModifyDialog.module.css';

const TITLE: Readonly<Record<SelectionModifyKind, string>> = {
  border: '边界选区',
  smooth: '平滑选区',
  expand: '扩展选区',
  contract: '收缩选区',
  feather: '羽化选区',
};

const DEFAULT_AMOUNT: Readonly<Record<SelectionModifyKind, number>> = {
  border: 5,
  smooth: 5,
  expand: 5,
  contract: 5,
  feather: 2,
};

export interface SelectionModifyDialogProps {
  readonly kind: SelectionModifyKind | null;
  readonly onClose: () => void;
  readonly onConfirm: (kind: SelectionModifyKind, amount: number) => void;
}

export function SelectionModifyDialog(props: SelectionModifyDialogProps): JSX.Element {
  const [amount, setAmount] = createSignal(5);
  let dialogRef: HTMLDivElement | undefined;
  useFocusTrap(() => props.kind !== null, () => dialogRef ?? null, props.onClose);

  // 每次换成一种新操作都重置成那种操作的默认值——组件实例贯穿应用生命周期
  createEffect(() => {
    const kind = props.kind;
    if (kind !== null) setAmount(DEFAULT_AMOUNT[kind]);
  });

  return (
    <Show when={props.kind}>
      {(kind) => (
        <div class={styles.backdrop} onClick={props.onClose} data-testid="selection-modify-backdrop">
          <div
            class={styles.dialog}
            role="dialog"
            ref={(el) => (dialogRef = el)}
            aria-label={TITLE[kind()]}
            data-testid="selection-modify-dialog"
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <h2 class={styles.title}>{TITLE[kind()]}</h2>

            <label class={styles.row}>
              <span class={styles.label}>像素数</span>
              <input
                type="number"
                class={styles.number}
                min="1"
                max="500"
                value={amount()}
                data-testid="selection-modify-amount"
                onInput={(event) => {
                  setAmount(Math.max(1, Math.round(Number(event.currentTarget.value) || 1)));
                }}
              />
              <span class={styles.value}>px</span>
            </label>

            <div class={styles.actions}>
              <button type="button" class={styles.secondary} onClick={props.onClose}>
                取消
              </button>
              <button
                type="button"
                class={styles.primary}
                data-testid="selection-modify-confirm"
                onClick={() => {
                  props.onConfirm(kind(), amount());
                }}
              >
                确定
              </button>
            </div>
          </div>
        </div>
      )}
    </Show>
  );
}
